"use client";

import { Button, cn, evaluate, Table, Tabs, Tooltip } from "@kyan/ui";

import { ComboRFQButton } from "./components/ComboRFQButton/ComboRFQButton";
import { EmptyState } from "./components/EmptyState";
import { deleteColumnId, tabs } from "./InstrumentsTable.constants";
import { useInstrumentsTableController } from "./InstrumentsTable.controller";

export const InstrumentsTable: React.FC = () => {
  const {
    columns,
    comboType,
    isLoading,
    hideButton,
    isRfqOptionsTab,
    comboInstruments,
    comboPerpInstruments,
    comboOptionInstruments,
    maxComboOptionInstruments,
    haveAllInstrumentsSizes,
    hasDuplicatedInstruments,
    setComboType,
    handleAddLeg,
    handleAddPerp,
    handleSubmit,
  } = useInstrumentsTableController();

  return (
    <div className="mt-[25px] grid">
      <div className="flex items-start justify-between">
        <Tabs
          items={tabs}
          value={comboType}
          variant="primary"
          itemClassName="h-12 max-w-52"
          className="-mb-px flex-1 pl-4"
          onChange={setComboType}
        />
        <div className="flex flex-1 justify-end gap-3">
          <Button
            className="w-47"
            variant="secondary"
            disabled={isRfqOptionsTab || comboPerpInstruments.length > 0}
            onClick={handleAddPerp}
          >
            + Add Perp
          </Button>
          <Button
            className="w-47 shadow-none"
            disabled={
              comboOptionInstruments.length >= maxComboOptionInstruments
            }
            variant={
              comboOptionInstruments.length === 0 ? "primary" : "secondary"
            }
            onClick={() => {
              handleAddLeg();
            }}
          >
            + Add Leg ({comboOptionInstruments.length}/
            {maxComboOptionInstruments})
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        data={comboInstruments}
        emptyState={<EmptyState />}
        classes={{
          table: "border border-divider rounded-sm",
          thead: "border-b border-divider",
          cellClassName: ({ row, column }) => {
            return cn("px-4 py-2", {
              "pointer-events-none opacity-30":
                row.type === "perps" &&
                isRfqOptionsTab &&
                column.id !== deleteColumnId,
            });
          },
          columnClassName: "px-4 py-2.75 items-center",
          row: "first:border-t-0",
          loading: {
            wrapper: "first:border-t-0",
            skeleton: "h-[34px]",
          },
        }}
        getRowId={(row) => row.id}
        renderRowEffect={({ row }) => {
          return (
            row.type === "perps" &&
            isRfqOptionsTab && (
              <Tooltip
                avoidCollisions
                side="bottom"
                align="center"
                content={
                  <div className="text-center">
                    RFQ supports Options only,
                    <br />
                    Perp cannot be included
                    <br />
                    in a combo trade.
                  </div>
                }
              >
                <button className="absolute size-full" />
              </Tooltip>
            )
          );
        }}
      />

      {!hideButton && (
        <>
          {isRfqOptionsTab ? (
            <ComboRFQButton />
          ) : (
            <Button
              size="lg"
              isLoading={isLoading}
              className="mt-4 w-47 place-self-end whitespace-nowrap shadow-none"
              disabled={
                !comboInstruments.length ||
                !haveAllInstrumentsSizes ||
                hasDuplicatedInstruments
              }
              onClick={handleSubmit}
            >
              {evaluate({
                "Duplicated instruments": hasDuplicatedInstruments,
                "Input size": !haveAllInstrumentsSizes,
                "Orderbook Prices": !isRfqOptionsTab,
                "Send RFQ": isRfqOptionsTab,
              })}
            </Button>
          )}
        </>
      )}
    </div>
  );
};
